import { getContext, setContext } from 'svelte';
import { type Readable, writable } from 'svelte/store';
import { browser } from '$app/environment';

import { assertExists } from '$lib/assert';

export const ActiveItemTypes = {
  SINGLETON: 'singleton',
  RESOURCE: 'resource',
  FRAGMENT_IMPORT: 'fragment-import',
} as const;

export const SingletonResourceNames = {
  MACHINE_SETTINGS: 'machine-settings',
  MAINTENANCE: 'maintenance',
  DESCRIPTION: 'description',
  DELETED_RESOURCE: 'deleted-resource',
} as const;

const singletonResourceName = new Set<string>(
  Object.values(SingletonResourceNames)
);

const activeItemType = Object.values(ActiveItemTypes);

export type ActiveItemType = (typeof activeItemType)[number];

export interface ActiveItem {
  type: ActiveItemType;
  parentID: string;
  name: string;
  resourceID?: string;
}

export interface ActiveItemContext {
  activeItem: Readable<ActiveItem | undefined>;
  setActiveItem: (newActiveItem: ActiveItem) => void;
  clearActiveItem: () => void;
}

export const ACTIVE_ITEM_CONTEXT_KEY = Symbol('active-item-context');

const getActiveItemFromURL = (): ActiveItem | undefined => {
  if (!browser) return undefined;
  const url = new URL(window.location.href);
  const params = url.searchParams.get('active-item-id');
  if (!params) return undefined;
  const resourceID =
    url.searchParams.get('active-item-resource-id') ?? undefined;
  return getActiveItemFromParams(params, resourceID);
};

export const createActiveItemContext = (): ActiveItemContext => {
  const activeItem = writable<ActiveItem | undefined>(getActiveItemFromURL());

  const setActiveItem = (newActiveItem: ActiveItem) => {
    activeItem.set(newActiveItem);

    const { type, parentID, name, resourceID } = newActiveItem;

    if (browser) {
      const url = new URL(window.location.href);
      url.searchParams.set('active-item-id', [type, parentID, name].join('/'));
      if (resourceID) {
        url.searchParams.set('active-item-resource-id', resourceID);
      } else {
        url.searchParams.delete('active-item-resource-id');
      }
      window.history.replaceState(window.history.state, '', url.toString());
    }
  };

  const clearActiveItem = () => {
    activeItem.set(undefined);

    if (browser) {
      const url = new URL(window.location.href);
      url.searchParams.delete('active-item-id');
      url.searchParams.delete('active-item-resource-id');
      window.history.replaceState(window.history.state, '', url.toString());
    }
  };

  return {
    activeItem,
    setActiveItem,
    clearActiveItem,
  };
};

export const provideActiveItemContext = (
  context = createActiveItemContext()
) => {
  setContext<ActiveItemContext>(ACTIVE_ITEM_CONTEXT_KEY, context);
  return context;
};

export const useActiveItem = (): ActiveItemContext => {
  const context = getContext<ActiveItemContext>(ACTIVE_ITEM_CONTEXT_KEY);
  assertExists(context, 'Active item context is not defined');
  return context;
};

export const isSingletonResource = (resourceName: string) => {
  return singletonResourceName.has(resourceName);
};

export const getActiveItemFromParams = (
  params: string,
  resourceID?: string
): ActiveItem | undefined => {
  const [type, parentID, ...rest] = params.split('/');
  const name = rest.join('/');
  if (!type || parentID === undefined || !name) return undefined;
  if (!activeItemType.includes(type as ActiveItemType)) {
    return undefined;
  }
  return {
    type: type as ActiveItemType,
    parentID,
    name,
    resourceID,
  };
};
